import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { FormInstance } from "antd/es/form/Form";
import { Checkbox, Col, Divider, Row, Table, Tooltip } from "antd";
import { uniqBy, uniq } from "lodash";
import type { TableColumn } from "../../../lib/interfaces";
import type { AgreementsSearchTableData } from "../AgreementsSearchModal/AgreementsSearchModal";
import { IClientType } from "../../../lib/types";
import { checkIsLoading, getFio } from "../../../lib/utils";
import { useAppSelector } from "../../..";
import ToggleColumns from "../../ToggleableVersionsTable/ToggleColumns";
import { shortNames } from "../../../lib/const/shortsNames";
import "./AgreementsSearchResult.less";

type ITableColumn = TableColumn<AgreementsSearchTableData>;

interface ComponentProps {
  multiselect: boolean;
  displayedPropertyNames: string[];
  clientType: IClientType;
  selectedAgreements: AgreementsSearchTableData[];
  setSelectedAgreements: Dispatch<SetStateAction<AgreementsSearchTableData[]>>;
  combineAgreements: boolean;
  form: FormInstance;
  tableRef: React.RefObject<HTMLDivElement>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

export const AgreementsSearchResult = (props: ComponentProps) => {
  const {
    multiselect,
    displayedPropertyNames,
    clientType,
    combineAgreements,
    selectedAgreements,
    setSelectedAgreements,
    form,
    tableRef,
    setCurrentPage,
  } = props;

  const { agreements, agreementsLoading, agreementsData } = useAppSelector(
    (state) => state.agreementsSearch
  );

  const tableData: AgreementsSearchTableData[] = useMemo((): AgreementsSearchTableData[] => {
    const selectedAgreementIds = selectedAgreements.map(({ id }) => id);
    const data: AgreementsSearchTableData[] = agreements
      .filter(({ account }) => {
        if (clientType === "ORGANIZATION") {
          return Boolean(account.organization);
        }
        if (clientType === "INDIVIDUAL") {
          return Boolean(account.individual);
        }
      })
      .sort(({ id: aId }, { id: bId }) => (aId > bId ? 1 : -1))
      .sort(({ agreementStatusId: aStatus }, { agreementStatusId: bStatus }) => {
        const statusOrder = (status: number) => (status === 6 ? 1 : 0);
        return statusOrder(aStatus) - statusOrder(bStatus);
      })
      .map((agreement) => {
        const accountAgreements = agreements // Для объединения договоров по одному аккаунту
          .filter(({ accountId }) => accountId === agreement.accountId)
          .sort((a, b) => (a.id < b.id ? 1 : -1)); // По ФТ нужно брать данные из первого договора
        const allExternalIds = accountAgreements.reduce<string[]>( // externalId по всем договорам аккаунта
          (result, { account }) => [
            ...result,
            ...account.subscribers.map(({ externalId }) => externalId),
          ],
          []
        );
        const fio = getFio({
          firstName: accountAgreements[0].account.individual?.firstName,
          lastName: accountAgreements[0].account.individual?.lastName,
          middleName: accountAgreements[0].account.individual?.middleName,
        });
        const fullName =
          clientType === "ORGANIZATION"
            ? accountAgreements[0].account.organization?.fullName
            : fio || String(accountAgreements[0].account.id);
        const inn =
          clientType === "ORGANIZATION"
            ? agreement.account.organization?.properties.find(
                ({ propertyTypeAlias }) => propertyTypeAlias === "INN"
              )?.stringValue
            : undefined;
        const kpp =
          clientType === "ORGANIZATION"
            ? agreement.account.organization?.properties.find(
                ({ propertyTypeAlias }) => propertyTypeAlias === "KPP"
              )?.stringValue
            : undefined;
        const bin =
          clientType === "ORGANIZATION"
            ? agreement.account.organization?.properties.find(
                ({ propertyTypeAlias }) => propertyTypeAlias === "BIN"
              )?.stringValue
            : undefined;

        const phones =
          clientType === "INDIVIDUAL"
            ? agreement.account.individual?.phones.map(
                ({ countryCode, phone }) => countryCode + phone
              )
            : undefined;

        const agreementNumber = combineAgreements
          ? accountAgreements.map(({ agreementNumber }) => agreementNumber)
          : [agreement.agreementNumber];

        const externalId = combineAgreements
          ? uniq(allExternalIds)
          : agreement.account.subscribers.map(({ externalId }) => externalId);

        return {
          key: agreement.id,
          accountId: agreement.accountId,
          fullName,
          agreementNumber,
          id: agreement.id,
          externalId,
          inn,
          kpp,
          bin,
          phones,
          selected: selectedAgreementIds.includes(agreement.id),
          fromDate: agreement.fromDate,
        };
      });

    return combineAgreements ? uniqBy(data, "accountId") : data;
  }, [agreements, clientType, combineAgreements, selectedAgreements]);

  const renderArrayItems = (arrData: (string | number)[]) => {
    if (arrData.length < 3) {
      return [arrData[0], arrData[1]].filter(Boolean).join(", ");
    }
    const title = arrData.map((item, idx) => <div key={idx}>{item}</div>);
    return (
      <Tooltip title={title}>{`${[arrData[0], arrData[1]].filter(Boolean).join(", ")}...`}</Tooltip>
    );
  };

  const allColumns: ITableColumn[] = useMemo(
    () =>
      [
        {
          key: "selected",
          width: "30px",
          constantlyDisplayed: multiselect,
          constantlyHidden: !multiselect,
          render: (v, { selected }) => <Checkbox checked={selected} />,
        },
        {
          title: "ID аккаунта",
          dataIndex: "accountId",
          key: "accountId",
          width: "100px",
          hidden: true,
        },
        {
          title: "Клиент",
          dataIndex: "fullName",
          key: "fullName",
          width: "200px",
        },
        {
          title: "Договор",
          dataIndex: "agreementNumber",
          key: "agreementNumber",
          width: "145px",
          render: (v, { agreementNumber }) => renderArrayItems(agreementNumber),
        },
        {
          title: "ID договора",
          dataIndex: "id",
          key: "id",
          width: "100px",
          hidden: true,
          constantlyHidden: combineAgreements,
        },
        {
          title: shortNames.externalId,
          dataIndex: "externalId",
          key: "externalId",
          width: "100px",
          render: (v, { externalId }) => renderArrayItems(externalId),
        },
        {
          title: "ИНН",
          dataIndex: "inn",
          key: "inn",
          width: "100px",
          constantlyHidden: clientType === "INDIVIDUAL" || !displayedPropertyNames.includes("inn"),
        },
        {
          title: "БИН",
          dataIndex: "bin",
          key: "bin",
          width: "100px",
          constantlyHidden: clientType === "INDIVIDUAL" || !displayedPropertyNames.includes("bin"),
        },
        {
          title: "КПП",
          dataIndex: "kpp",
          key: "kpp",
          width: "100px",
          hidden: true,
          constantlyHidden: clientType === "INDIVIDUAL" || !displayedPropertyNames.includes("kpp"),
        },
        {
          title: "Телефон",
          dataIndex: "phones",
          key: "phones",
          // width: "20%",
          constantlyHidden: clientType === "ORGANIZATION",
          render: (v, { phones }) => phones && renderArrayItems(phones),
        },
      ]
        .filter(({ constantlyHidden }) => !constantlyHidden)
        .map((item) => item as ITableColumn), // Почему-то меняются типы при использовании filter, поэтому приходится привести
    // ],
    [clientType, displayedPropertyNames, combineAgreements]
  );

  const [columns, setColumns] = useState<ITableColumn[]>(allColumns);

  // console.log(columns);

  const tableTitle = () => (
    <>
      <Row justify="space-between">
        <Col>
          <h3>Результаты поиска</h3>
        </Col>
        <Col>
          <ToggleColumns
            columns={columns}
            setColumns={setColumns}
            tableName={`agreements-search-${clientType}`}
            notHiddenColumnIndex={multiselect ? 2 : 1}
          />
        </Col>
      </Row>
      <Divider style={{ margin: 0, width: "794px" }} />
    </>
  );

  // console.log(tableData);

  const onSelectAgreement = (agreement: AgreementsSearchTableData) => {
    const indexOfSelectedAgreement = selectedAgreements.findIndex(({ id }) => id === agreement.id);
    switch (multiselect) {
      case true:
        if (indexOfSelectedAgreement < 0) {
          setSelectedAgreements((prev) => [...prev, agreement]);
        } else {
          setSelectedAgreements((prev) => [
            ...prev.slice(0, indexOfSelectedAgreement),
            ...prev.slice(indexOfSelectedAgreement + 1),
          ]);
        }
        break;
      case false:
        if (indexOfSelectedAgreement < 0) {
          setSelectedAgreements([agreement]);
        } else {
          setSelectedAgreements([]);
        }
        break;
    }
  };

  const onSelectAllAgreements = () => {
    if (selectedAgreements.length < tableData.length) {
      setSelectedAgreements(tableData);
    } else {
      setSelectedAgreements([]);
    }
  };

  // Функция для проверки, достигнут ли нижний край таблицы
  const handleScroll = useCallback(() => {
    const table = tableRef.current?.querySelector(".ant-table-body");
    if (!table || !agreementsData) return;

    const { scrollTop, scrollHeight, clientHeight } = table;
    const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 50;

    // Проверяем, есть ли еще элементы для загрузки
    const hasMoreElements = agreements.length < agreementsData.totalElements;

    if (isNearBottom && !checkIsLoading(agreementsLoading) && hasMoreElements) {
      // Увеличиваем номер страницы и отправляем форму
      setCurrentPage((prev) => prev + 1);
      form.submit();
    }
  }, [agreementsLoading, agreementsData, form]);

  // Добавляем обработчик скролла
  useEffect(() => {
    const table = tableRef.current?.querySelector(".ant-table-body");
    if (table) {
      table.addEventListener("scroll", handleScroll);
      return () => {
        table.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <div className="agreements-search-result" ref={tableRef}>
      {multiselect && (
        <Checkbox
          className="agreements-search-result__checkAll"
          disabled={tableData.length === 0}
          indeterminate={
            tableData.length > 0 &&
            selectedAgreements.length > 0 &&
            selectedAgreements.length !== tableData.length
          }
          checked={tableData.length > 0 && selectedAgreements.length === tableData.length}
          onChange={onSelectAllAgreements}
        />
      )}
      <Table
        style={{ padding: 0, height: "270px", marginTop: "10px" }}
        className="table_no-hover"
        title={tableTitle}
        columns={columns.filter(({ hidden }) => !hidden)}
        dataSource={tableData}
        loading={checkIsLoading(agreementsLoading)}
        pagination={false}
        size="small"
        scroll={{
          y: "152px",
        }}
        onRow={(agreement) => ({ onClick: () => onSelectAgreement(agreement) })}
        rowClassName={({ selected }) =>
          selected
            ? "agreements-search-result__table-row_selected"
            : "agreements-search-result__table-row"
        }
      />
    </div>
  );
};
